import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Plus, QrCode as QrCodeIcon, Loader2, ArrowRight } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { toast } from '../../../hooks/use-toast';
import { QR_TYPES } from '../../../lib/qrEncode';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const typeLabel = (value) => QR_TYPES.find((t) => t.value === value)?.label || value;

const QrBuilderDashboard = ({ basePath = '/admin/qr-builder' }) => {
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/qr-codes`);
        setCodes(res.data);
      } catch (error) {
        toast({ title: 'Error', description: 'Could not load your QR codes.', variant: 'destructive' });
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <div className="max-w-6xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">QR Builder</h1>
          <p className="text-gray-500 text-sm">Create and manage your QR codes.</p>
        </div>
        <Button asChild className="bg-[#FF6B00] hover:bg-[#e05f00]">
          <Link to={`${basePath}/new`}><Plus className="w-4 h-4 mr-2" /> Create New</Link>
        </Button>
      </div>

      <div className="grid sm:grid-cols-2 gap-4 mb-8">
        <Card>
          <CardContent className="p-5 flex items-center gap-4">
            <div className="p-3 bg-orange-50 rounded-xl">
              <QrCodeIcon className="w-6 h-6 text-[#FF6B00]" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{loading ? '—' : codes.length}</p>
              <p className="text-gray-500 text-sm">QR codes saved</p>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-base">Recent QR Codes</CardTitle>
          {codes.length > 0 && (
            <Link to={`${basePath}/codes`} className="text-sm text-[#FF6B00] hover:underline flex items-center gap-1">
              View all <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          )}
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-[#FF6B00]" />
            </div>
          ) : codes.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-500 mb-4">You haven't created any QR codes yet.</p>
              <Button asChild className="bg-[#FF6B00] hover:bg-[#e05f00]">
                <Link to={`${basePath}/new`}><Plus className="w-4 h-4 mr-2" /> Create Your First QR Code</Link>
              </Button>
            </div>
          ) : (
            <div className="divide-y">
              {codes.slice(0, 5).map((qr) => (
                <Link
                  key={qr.id}
                  to={`${basePath}/edit/${qr.id}`}
                  className="flex items-center justify-between py-3 hover:bg-gray-50 -mx-2 px-2 rounded-lg transition-colors"
                >
                  <div>
                    <p className="font-medium text-gray-900">{qr.name}</p>
                    <p className="text-xs text-gray-500">{typeLabel(qr.type)}</p>
                  </div>
                  <p className="text-xs text-gray-400">{new Date(qr.created_at).toLocaleDateString()}</p>
                </Link>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default QrBuilderDashboard;
